import type { DestinationMetricsSnapshot, DestinationResult } from './types';

type DestinationCounters = {
  sent: number;
  failed: number;
  ignored: number;
  skipped: number;
  totalDuration: number;
  pageViewSent: number;
  pageViewDeduped: number;
  pageViewFailed: number;
  scriptLoadAttempts: number;
  scriptLoadSuccess: number;
  scriptLoadFailure: number;
  initAttempts: number;
  initSuccess: number;
  initFailure: number;
  lastError: string | null;
};

function emptyCounters(): DestinationCounters {
  return {
    sent: 0,
    failed: 0,
    ignored: 0,
    skipped: 0,
    totalDuration: 0,
    pageViewSent: 0,
    pageViewDeduped: 0,
    pageViewFailed: 0,
    scriptLoadAttempts: 0,
    scriptLoadSuccess: 0,
    scriptLoadFailure: 0,
    initAttempts: 0,
    initSuccess: 0,
    initFailure: 0,
    lastError: null,
  };
}

export class DestinationMetrics {
  private registeredDestinations = 0;

  private eventsSent = 0;

  private eventsFailed = 0;

  private eventsIgnored = 0;

  private eventsSkipped = 0;

  private pageViewReceived = 0;

  private pageViewSent = 0;

  private pageViewSkipped = 0;

  private pageViewDeduped = 0;

  private pageViewFailed = 0;

  private scriptLoadAttempts = 0;

  private scriptLoadSuccess = 0;

  private scriptLoadFailure = 0;

  private initAttempts = 0;

  private initSuccess = 0;

  private initFailure = 0;

  private totalDuration = 0;

  private lastExecutionAt: number | null = null;

  private lastErrorAt: number | null = null;

  private lastErrorMessage: string | null = null;

  private readonly perDestination = new Map<string, DestinationCounters>();

  private readonly perDestinationId = new Map<
    string,
    {
      sent: number;
      failed: number;
      pageViewSent: number;
      pageViewDeduped: number;
      pageViewFailed: number;
    }
  >();

  private getDestinationCounters(name: string): DestinationCounters {
    let counters = this.perDestination.get(name);
    if (!counters) {
      counters = emptyCounters();
      this.perDestination.set(name, counters);
    }
    return counters;
  }

  setRegisteredCount(count: number): void {
    this.registeredDestinations = count;
  }

  recordPageViewReceived(): void {
    this.pageViewReceived += 1;
  }

  recordScriptLoadAttempt(adapter: string): void {
    this.scriptLoadAttempts += 1;
    this.getDestinationCounters(adapter).scriptLoadAttempts += 1;
  }

  recordScriptLoadSuccess(adapter: string): void {
    this.scriptLoadSuccess += 1;
    this.getDestinationCounters(adapter).scriptLoadSuccess += 1;
  }

  recordScriptLoadFailure(adapter: string): void {
    this.scriptLoadFailure += 1;
    this.getDestinationCounters(adapter).scriptLoadFailure += 1;
  }

  recordInitAttempt(adapter: string): void {
    this.initAttempts += 1;
    this.getDestinationCounters(adapter).initAttempts += 1;
  }

  recordInitSuccess(adapter: string): void {
    this.initSuccess += 1;
    this.getDestinationCounters(adapter).initSuccess += 1;
  }

  recordInitFailure(adapter: string): void {
    this.initFailure += 1;
    this.getDestinationCounters(adapter).initFailure += 1;
  }

  record(result: DestinationResult): void {
    const adapter = result.adapter;
    const counters = this.getDestinationCounters(adapter);

    this.lastExecutionAt = result.timestamp;
    this.totalDuration += result.duration;

    if (result.status === 'success') {
      this.eventsSent += 1;
      counters.sent += 1;
    } else if (result.status === 'failed') {
      this.eventsFailed += 1;
      counters.failed += 1;
      this.lastErrorAt = result.timestamp;
      this.lastErrorMessage = result.error ?? 'Unknown error';
      counters.lastError = this.lastErrorMessage;
    } else if (result.status === 'ignored') {
      this.eventsIgnored += 1;
      counters.ignored += 1;
    } else if (result.status === 'skipped') {
      this.eventsSkipped += 1;
      counters.skipped += 1;
    }

    counters.totalDuration += result.duration;

    if (result.metadata) {
      const sent = Number(result.metadata.sent ?? 0);
      const deduped = Number(result.metadata.deduped ?? 0);
      const failed = Number(result.metadata.failed ?? 0);

      if (sent > 0) {
        this.pageViewSent += sent;
        counters.pageViewSent += sent;
      }
      if (deduped > 0) {
        this.pageViewDeduped += deduped;
        counters.pageViewDeduped += deduped;
      }
      if (failed > 0) {
        this.pageViewFailed += failed;
        counters.pageViewFailed += failed;
      }

      const pixelIds = result.metadata.pixelIds as string[] | undefined;
      const tagIds = result.metadata.tagIds as string[] | undefined;
      const ids = pixelIds ?? tagIds;
      if (ids && sent > 0) {
        for (const id of ids.slice(0, sent)) {
          this.recordDestinationId(adapter, id, 'sent', sent / ids.length);
        }
      }
    }

    if (result.status === 'failed' && result.error) {
      this.pageViewFailed += 1;
      counters.pageViewFailed += 1;
    }
  }

  private recordDestinationId(
    adapter: string,
    id: string,
    kind: 'sent' | 'failed',
    count: number
  ): void {
    const key = `${adapter}:${id}`;
    let counters = this.perDestinationId.get(key);
    if (!counters) {
      counters = {
        sent: 0,
        failed: 0,
        pageViewSent: 0,
        pageViewDeduped: 0,
        pageViewFailed: 0,
      };
      this.perDestinationId.set(key, counters);
    }
    if (kind === 'sent') {
      counters.sent += count;
      counters.pageViewSent += count;
    } else {
      counters.failed += count;
      counters.pageViewFailed += count;
    }
  }

  getSnapshot(): DestinationMetricsSnapshot {
    const total = this.eventsSent + this.eventsFailed;
    const perDestination: Record<
      string,
      DestinationMetricsSnapshot['perDestination'][string]
    > = {};

    for (const [name, counters] of this.perDestination) {
      const destTotal = counters.sent + counters.failed;
      perDestination[name] = {
        sent: counters.sent,
        failed: counters.failed,
        ignored: counters.ignored,
        skipped: counters.skipped,
        averageDuration: destTotal > 0 ? counters.totalDuration / destTotal : 0,
        successRate: destTotal > 0 ? counters.sent / destTotal : 0,
        pageViewSent: counters.pageViewSent,
        pageViewDeduped: counters.pageViewDeduped,
        pageViewFailed: counters.pageViewFailed,
        scriptLoadAttempts: counters.scriptLoadAttempts,
        scriptLoadSuccess: counters.scriptLoadSuccess,
        scriptLoadFailure: counters.scriptLoadFailure,
        initAttempts: counters.initAttempts,
        initSuccess: counters.initSuccess,
        initFailure: counters.initFailure,
        lastError: counters.lastError,
      };
    }

    return {
      registeredDestinations: this.registeredDestinations,
      activeDestinations: this.registeredDestinations,
      eventsSent: this.eventsSent,
      eventsFailed: this.eventsFailed,
      eventsIgnored: this.eventsIgnored,
      eventsSkipped: this.eventsSkipped,
      pageViewReceived: this.pageViewReceived,
      pageViewSent: this.pageViewSent,
      pageViewSkipped: this.pageViewSkipped,
      pageViewDeduped: this.pageViewDeduped,
      pageViewFailed: this.pageViewFailed,
      scriptLoadAttempts: this.scriptLoadAttempts,
      scriptLoadSuccess: this.scriptLoadSuccess,
      scriptLoadFailure: this.scriptLoadFailure,
      initAttempts: this.initAttempts,
      initSuccess: this.initSuccess,
      initFailure: this.initFailure,
      averageDuration: total > 0 ? this.totalDuration / total : 0,
      successRate: total > 0 ? this.eventsSent / total : 0,
      lastExecutionAt: this.lastExecutionAt,
      lastErrorAt: this.lastErrorAt,
      lastErrorMessage: this.lastErrorMessage,
      perDestination,
      perDestinationId: Object.fromEntries(this.perDestinationId),
    };
  }

  reset(): void {
    this.registeredDestinations = 0;
    this.eventsSent = 0;
    this.eventsFailed = 0;
    this.eventsIgnored = 0;
    this.eventsSkipped = 0;
    this.pageViewReceived = 0;
    this.pageViewSent = 0;
    this.pageViewSkipped = 0;
    this.pageViewDeduped = 0;
    this.pageViewFailed = 0;
    this.scriptLoadAttempts = 0;
    this.scriptLoadSuccess = 0;
    this.scriptLoadFailure = 0;
    this.initAttempts = 0;
    this.initSuccess = 0;
    this.initFailure = 0;
    this.totalDuration = 0;
    this.lastExecutionAt = null;
    this.lastErrorAt = null;
    this.lastErrorMessage = null;
    this.perDestination.clear();
    this.perDestinationId.clear();
  }
}
